export const BOOKING_FLOW_STEPS = [
  'Service',
  'Address',
  'Date & Time',
  'Contact',
  'Confirm',
] as const

export function clampBookingQuantity(
  value: number,
  maxQuantity: number | null | undefined
): number {
  const safeMax = Math.max(1, maxQuantity ?? 10)

  if (!Number.isFinite(value)) {
    return 1
  }

  return Math.max(1, Math.min(safeMax, Math.trunc(value)))
}

type SlotLookupGateInput = {
  serviceId: string | null | undefined
  date: string | null | undefined
  slotLookupAllowed: boolean | null | undefined
  customerH3: string | null | undefined
}

export type BookingSlotLookupFailureResult =
  | 'no_feasible_slots'
  | 'outside_service_area'
  | 'booking_unavailable'
  | 'degraded_unavailable'

export type BookingSlotRecoveryAction = 'change_address' | 'change_service' | 'choose_another_date'

export function canLookupFeasibleSlots(input: SlotLookupGateInput): boolean {
  return Boolean(input.serviceId && input.date && input.slotLookupAllowed && input.customerH3)
}

export function areRequiredBookingResponsesComplete(
  fields: Array<{ id: string; isRequired: boolean }>,
  responses: Record<string, unknown>
): boolean {
  return fields.every((field) => {
    if (!field.isRequired) return true

    const value = responses[field.id]
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'string') return value.trim().length > 0
    if (typeof value === 'boolean') return true
    if (typeof value === 'number') return Number.isFinite(value)

    return value != null
  })
}

export function getSlotLookupRecoveryState(result: BookingSlotLookupFailureResult): {
  title: string
  description: string
  actions: BookingSlotRecoveryAction[]
} {
  switch (result) {
    case 'no_feasible_slots':
      return {
        title: 'No route-feasible times on this date',
        description:
          'Try another date, change the service address, or choose a different offering to keep going.',
        actions: ['choose_another_date', 'change_address', 'change_service'],
      }
    case 'outside_service_area':
      return {
        title: 'This address is outside the service area',
        description:
          'Choose a different address or another offering before looking up times again.',
        actions: ['change_address', 'change_service'],
      }
    case 'booking_unavailable':
      return {
        title: 'Booking is unavailable right now',
        description:
          'Try another date window or choose a different offering while this service is unavailable.',
        actions: ['choose_another_date', 'change_service'],
      }
    case 'degraded_unavailable':
      return {
        title: 'Travel-aware availability is temporarily unavailable',
        description:
          'Try another date window or update the address while travel-aware slot computation recovers.',
        actions: ['choose_another_date', 'change_address'],
      }
  }
}
